import React, { MutableRefObject, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { Controller, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { AnimatePresence } from "framer-motion"
import theme from "lib/material-ui/theme"
import Stack from "@mui/material/Stack"
import { styled } from "@mui/material/styles"
import { debounce } from "@mui/material"
import Slide from "@mui/material/Slide"
import { AppDispatch } from "appRedux/store"
import { useAppSelector } from "appRedux/hooks"
import { updateItemFormFields, updateShowChooseListsDropSheet } from "appRedux/slices/itemFormSlice"
import { resetListFormSlice } from "appRedux/slices/listFormSlice"
import { updateGlobalToastMessage } from "appRedux/slices/globalSlice"
import { wishItemRequestSchema } from "validations/wishItem"
import { DEBOUNCE_WAIT, EDIT, IRT } from "constants/index"
import { DISCARD_IMAGE_CROP, SAVE_CROPPED_IMAGE, SET_ORIGINAL_IMAGE } from "constants/events"
import { formatThousands, removeSeparator } from "utils/numberUtils"
import { uploadImage } from "utils/componentsUtils"
import useWishLists from "hooks/useWishLists"
import useBackListener from "hooks/useBackListener"
import useEventListener from "hooks/useEventListener"
import AddImageIcon from "components/atoms/_icons/AddImageIcon"
import AddIcon from "components/atoms/_icons/AddIcon"
import TrashIcon from "components/atoms/_icons/TrashIcon"
import EditIcon from "components/atoms/_icons/EditIcon"
import ArrowIcon from "components/atoms/_icons/ArrowIcon"
import TextField from "components/atoms/TextField"
import CTA from "components/atoms/CTA"
import IconWrapper from "components/atoms/IconWrapper"
import LazyImage from "components/atoms/LazyImage"
import ImageCropperModal from "components/atoms/ImageCropperModal"
import ImageInputWithCropperModal from "components/molecules/ImageInputWithCropperModal"
import SelectListDropSheetToAddItems from "components/organisms/SelectListDropSheetToAddItems"
import CreateNewListOverlay from "components/templates/WishItemForm/CreateNewListOverlay"
import CurrencySection from "components/templates/WishItemForm/CurrencySection"

type WishItemFormProps = {
  isCTADisabled: (dirtyFields: object) => boolean;
  isCTALoading: () => boolean;
  onSubmit: (value: WishItemRequestPayload) => void;
}

export default function WishItemForm(props: WishItemFormProps) {
  const { isCTADisabled, isCTALoading, onSubmit } = props

  const [image, setImage] = useState<string | File | null>(null)
  const [originalImage, setOriginalImage] = useState<string | null>(null)
  const [showEnlargedImage, setShowEnlargedImage] = useState<boolean>(false)
  const [showCropOriginalImageModal, setShowCropOriginalImageModal] = useState<boolean>(false)
  const [fileName, setFileName] = useState<string>("")

  const containerRef = useRef<HTMLDivElement | null>(null)
  const enlargedImageRef = useRef<HTMLDivElement | null>(null)

  const wishListState = useAppSelector((state) => state.wishList)
  const formFields = useAppSelector((state) => state.itemForm.formFields)
  const itemFormMode = useAppSelector((state) => state.itemForm.mode)
  const showChooseListsDropSheet = useAppSelector((state) => state.itemForm.showChooseListsDropSheet)
  const showCreateNewListOverlay = useAppSelector((state) => state.itemForm.showCreateNewListOverlay)
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)
  const currencies = useAppSelector((state) => state.initSlice.supportedCurrencies.currencies)
  const defaultCurrency = useAppSelector((state) => state.initSlice.supportedCurrencies.defaultCurrency)

  useWishLists()

  const { t } = useTranslation()
  const dispatch = useDispatch() as AppDispatch
  const navigate = useNavigate()

  useBackListener(showEnlargedImage, handleBackListenerCallback, handleCloseChooseListsDropSheet)

  useBackListener(showCropOriginalImageModal, handleCropOriginalImageBackListenerCallback)

  useEventListener(document, SET_ORIGINAL_IMAGE, handleSetOriginalImage)

  useEventListener(document, SAVE_CROPPED_IMAGE, handleSaveCroppedImage)

  useEventListener(document, DISCARD_IMAGE_CROP, handleDiscardImageCrop)

  const {
    control,
    register,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors, dirtyFields }
  } = useForm<WishItemRequestPayload>({
    resolver: zodResolver(wishItemRequestSchema),
    shouldFocusError: false,
    reValidateMode: "onBlur",
    defaultValues: formFields
  })

  useEffect(() => {
    setDefaultValueFormFields(formFields)
  }, [formFields])

  useEffect(() => {
    setDefaultImageValue(formFields.image)
  }, [formFields.image])

  useEffect(() => {
    if (!Object.keys(errors).length) return
    console.log(errors)
    if (errors.url) {
      dispatch(updateGlobalToastMessage({
        severity: "error",
        key: `invalidUrl`,
        show: true,
        status: 400,
        message: t("general.invalidUrl")
      }))
    }
  }, [errors])

  useEffect(() => {
    if (!wishListState?.listInfo || itemFormMode === EDIT) return
    setValue("wishlistIds", [wishListState.listInfo.id])
  }, [wishListState?.listInfo])

  useEffect(() => {
    if (!showCreateNewListOverlay && currentPageInfo?.path === "/item") {
      dispatch(resetListFormSlice())
    }
  }, [showCreateNewListOverlay])

  useEffect(() => {
    if (!showEnlargedImage) return
    if (!image) return
    if (typeof image === "string" && !image.startsWith("data:image/")) return
    if (typeof image === "string") {
      if (!enlargedImageRef.current) return
      enlargedImageRef.current.style.backgroundImage = `url(${image})`
      enlargedImageRef.current.style.backgroundSize = "contain"
      enlargedImageRef.current.style.backgroundRepeat = "no-repeat"
      enlargedImageRef.current.style.backgroundPosition = "center"
    } else {
      setImageFileAsContainerBackground(image, enlargedImageRef)
    }
  }, [showEnlargedImage])

  const shouldRenderPriceSection = useMemo(() => {
    return currencies && currencies.length > 0
  }, [currencies, defaultCurrency])

  function handleBackListenerCallback() {
    setShowEnlargedImage(false)
  }

  function handleCropOriginalImageBackListenerCallback() {
    setShowCropOriginalImageModal(false)
  }

  function handleSetOriginalImage(event: CustomEvent) {
    const detail = event.detail
    setOriginalImage(detail.image)
    setFileName(detail.filename)
  }

  function handleSaveCroppedImage(event: CustomEvent) {
    if (showCropOriginalImageModal) {
      let container
      if (containerRef?.current) {
        container = containerRef?.current
      }
      if (!container) return
      const { blob } = event.detail
      const file = new File([blob], fileName, { type: "image/jpeg" })
      const customEventObj = { target: { files: [file] } }
      uploadImage(customEventObj, container, setImage, setItemImage)
      setShowCropOriginalImageModal(false)
    }
  }

  function handleDiscardImageCrop() {
    if (showCropOriginalImageModal) {
      setShowCropOriginalImageModal(false)
    }
  }

  function setDefaultValueFormFields(formFields: WishItemRequestPayload) {
    if (JSON.stringify(formFields) === JSON.stringify(getValues())) return
    reset({
      ...formFields,
      wishlistIds: [...formFields.wishlistIds]
    })
  }

  function setDefaultImageValue(image: string | File) {
    if (image && typeof image === "string" && image.length) {
      setSavedImageAsDefault(image)
    } else if ((image instanceof File && image.size === 0) || (typeof image === "string" && image.length === 0)) {
      resetImage()
    } else if (image instanceof File && image.size > 0) {
      setImageFileAsContainerBackground(image, containerRef)
    }
  }

  function setSavedImageAsDefault(image: string) {
    if (!containerRef.current) return
    setImage(image)
    containerRef.current.style.background = `url("${process.env.REACT_APP_API_URL}/${image.replace(/ /g, "%20")}"), ${theme.palette.gray[16]}`
  }

  function resetImage() {
    if (!containerRef.current) return
    containerRef.current.style.background = "none"
    setImage(null)
  }

  function setImageFileAsContainerBackground(image: File, ref: MutableRefObject<HTMLDivElement | null>) {
    if (!ref.current) return
    if (image) {
      const fileReader = new FileReader()

      fileReader.onload = () => {
        if (!ref.current) return
        ref.current.style.background = `url(${fileReader.result as string}), ${theme.palette.gray[16]}`
        ref.current.style.backgroundSize = "contain"
        setImage(fileReader.result as string)
      }

      fileReader.readAsDataURL(image)
    }
  }

  function setItemImage(value: File | string) {
    setValue("image", value, { shouldDirty: true })
    setFormValuesToRedux({
      ...formFields,
      image: value
    })
  }

  function handleDeleteImage() {
    setImage(null)
    setItemImage("")
    if (containerRef.current) {
      containerRef.current.style.backgroundImage = ""
    }
    if (originalImage) {
      setOriginalImage(null)
    }
  }

  function handleEditSelectedImage() {
    setShowCropOriginalImageModal(true)
  }

  function handleShowChooseListsDropSheet() {
    dispatch(updateShowChooseListsDropSheet(true))
  }

  function handleCloseChooseListsDropSheet() {
    dispatch(updateShowChooseListsDropSheet(false))
  }

  function handleSubmitSelectedListsForItem(lists: number[]) {
    handleCloseChooseListsDropSheet()
    setValue("wishlistIds", lists, { shouldDirty: true })
    handleOnFormBlur()
  }

  function handleSelectCurrency(currency: string) {
    setValue("currency", currency, { shouldDirty: true })
    handleOnFormBlur()
    if (currency !== IRT) {
      setValue("price", removeSeparator(String(getValues("price"))))
    }
  }

  const handleOnFormBlur = debounce(function () {
    const formFieldsValue = getValues()
    setFormValuesToRedux(formFieldsValue)
  }, DEBOUNCE_WAIT)

  function handleImageContainerClicked(event: React.MouseEvent) {
    // @ts-ignore
    if (event?.target?.id !== "item-image-container") return
    if (!image) return
    if (image instanceof File && image.size === 0) return
    setShowEnlargedImage(true)
  }

  function handleBackButtonClicked() {
    setShowEnlargedImage(false)
    navigate(-1)
  }

  function setFormValuesToRedux(values: WishItemRequestPayload) {
    dispatch(updateItemFormFields(values))
  }

  function handlePriceInputChange(event: React.ChangeEvent<HTMLInputElement>) {
    let value = event.target.value
    if (getValues("currency") !== IRT) return value
    return formatThousands(value)
  }

  function getPrice() {
    const price = getValues("price")
    if (!price) return price
    const currency = getValues("currency")
    if (currency !== IRT) return String(price)
    return formatThousands(price)
  }

  return (
    <>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        onChange={handleOnFormBlur}
      >
        <Stack alignItems="center">
          <ImageWrapper style={{
            backgroundColor: image ? "#FFFFFF" : theme.palette.primary["1"],
            border: `1px ${image ? "solid" : "dashed"} ${image ? theme.palette.gray["7"] : theme.palette.primary["6"]}`
          }}>
            <ImageContainer ref={containerRef} id="item-image-container" onClick={handleImageContainerClicked}>
              {image ?
                <ImageActionsContainer></ImageActionsContainer>
                : <ImageInputContainer>
                  <ImageInputWithCropperModal
                    id="image"
                    register={register}
                    setValue={setValue}
                    containerRef={containerRef}
                    cropperAspectRatio={NaN}
                    cropShape="rect"
                    setImage={setImage}
                    onChange={setItemImage}
                  >
                    <AddImageIconWrapper>
                      <AddImageIcon/>
                    </AddImageIconWrapper>
                  </ImageInputWithCropperModal>
                </ImageInputContainer>}
            </ImageContainer>

            {image && <Stack flexDirection="row">
              {originalImage && <IconWrapper id="edit" onClick={handleEditSelectedImage}>
                  <EditIcon/>
              </IconWrapper>}
                <IconWrapper onClick={handleDeleteImage}>
                    <TrashIcon/>
                </IconWrapper>
            </Stack>}
          </ImageWrapper>

          <TextField
            id="name"
            label={t("items.itemName")}
            defaultValue={getValues("name")}
            register={register}
            setValue={setValue}
            error={errors.name}
          />
          <TextField
            id="url"
            label={t("items.link")}
            defaultValue={getValues("url")}
            type="url"
            register={register}
            setValue={setValue}
            error={errors.url}
          />
          <TextField
            id="caption"
            label={t("items.description")}
            defaultValue={getValues("caption")}
            register={register}
            setValue={setValue}
            error={errors.caption}
          />
          {shouldRenderPriceSection && <TextField
              id="price"
              label={t("general.price")}
              defaultValue={getPrice()}
              register={register}
              setValue={setValue}
              error={errors.price}
              mandatoryIcon={true}
              type="text"
              pattern="[0-9]*"
              endAdornmentIcon={<Controller
                name="currency"
                control={control}
                render={({ field: { value } }) =>
                  <CurrencySection
                    selectedCurrency={value}
                    defaultCurrency={defaultCurrency}
                    currencies={currencies}
                    onChange={(currency) => handleSelectCurrency(currency)}
                  />}
              />}
              onChange={handlePriceInputChange}
          />}
          <Controller
            name="wishlistIds"
            control={control}
            render={({ field: { value } }) =>
              <AddToListButtonWrapper>
                <CTA
                  variant={"text"}
                  endIcon={<AddIcon/>}
                  onClick={handleShowChooseListsDropSheet}
                >
                  {value?.length ? <SelectedListsCount>
                    ({value.length} {t("items.listsSelected")})
                  </SelectedListsCount> : null}
                  {t("items.addToList")}
                </CTA>
              </AddToListButtonWrapper>
            }
          />
        </Stack>

        <CTA
          variant="contained"
          type="submit"
          disabled={isCTADisabled(dirtyFields)}
          loading={isCTALoading()}
          onClick={handleSubmit(onSubmit)}
        >
          {t("general.save")}
        </CTA>
      </Form>
      <SelectListDropSheetToAddItems
        show={showChooseListsDropSheet}
        defaultSelectedLists={getValues("wishlistIds")}
        onOpen={handleShowChooseListsDropSheet}
        onClose={handleCloseChooseListsDropSheet}
        onSubmit={handleSubmitSelectedListsForItem}
      />
      <CreateNewListOverlay
        show={showCreateNewListOverlay}
      />
      <Slide
        id="full-screen-item-image-modal"
        direction="left"
        in={showEnlargedImage && !!image}
        easing={{
          enter: "ease-in-out",
          exit: "linear"
        }}
        style={{ position: "fixed", top: 0, left: 0 }}
      >
        <EnlargedImageContainer>
          <IconWrapper id="back-button" onClick={handleBackButtonClicked}>
            <ArrowIcon/>
          </IconWrapper>

          <EnlargedImageWrapper ref={enlargedImageRef} id="enlarged-image-wrapper">
            {(image && typeof image === "string" && !image.startsWith("data:image/")) ? <LazyImage
              src={image && `${process.env.REACT_APP_API_URL}/${image}`}
              alt="image"
              theme="dark"
            /> : null}
          </EnlargedImageWrapper>

          <div style={{ width: "44px", height: "44px" }}></div>
        </EnlargedImageContainer>
      </Slide>

      <AnimatePresence>
        <ImageCropperModal
          show={showCropOriginalImageModal}
          loading={false}
          aspectRatio={NaN}
          cropShape="rect"
          src={originalImage}
        />
      </AnimatePresence>
    </>
  )
}

const Form = styled("form")(() => ({
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  flexGrow: 1,
  paddingTop: "8px"
}))

const ImageWrapper = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100px",
  borderRadius: "16px",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "flex-end",
  position: "relative",
  marginBottom: "50px",
  padding: "8px",

  "& #edit svg": {
    stroke: theme.palette.gray["14"]
  },

  "& input": {
    display: "none"
  }
}))

const ImageContainer = styled("div")(({ theme }) => ({
  width: "100px",
  height: "100%",
  borderRadius: "8px",
  backgroundColor: theme.palette.gray["3"],
  backgroundSize: "contain !important",
  backgroundPosition: "center !important",
  backgroundRepeat: "no-repeat !important"
}))

const ImageInputContainer = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  borderRadius: "8px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",

  "& svg": {
    width: "24px",
    height: "24px",
    stroke: theme.palette.primary["5"]
  }
}))

const AddImageIconWrapper = styled(Stack)(({ theme }) => ({
  width: "40px",
  height: "40px",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "50%",
  backgroundColor: theme.palette.primary[6],

  "& svg": {
    stroke: "#FFFFFF"
  }
}))

const ImageActionsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: "8px",
  position: "absolute",
  bottom: "4px",
  right: "8px",

  "& div": {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: theme.palette.primary["1"],

    "& svg": {
      stroke: theme.palette.gray["14"]
    }
  }
}))

const AddToListButtonWrapper = styled("div")(({ theme }) => ({
    alignSelf: "flex-start",
    marginTop: "-36px",

    "& svg": {
      width: 16,
      height: 16,
      stroke: theme.palette.primary["6"]
    }
  }
))

const SelectedListsCount = styled("span")(({ theme }) => ({
  fontSize: "1.1rem",
  fontWeight: 400,
  color: theme.palette.gray["13"],
  margin: "0 4px",
  fontFamily: "VazirmatnFD, serif !important"
}))

const EnlargedImageContainer = styled(Stack)(({ theme }) => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "flex-end",
  backgroundColor: theme.palette.gray["16"],
  transform: "translateX(-50%)",
  zIndex: 10,

  "@media (min-width: 441px)": {
    right: "calc((100vw - var(--max-container-width)) / 2) !important"
  },

  "& svg": {
    stroke: "#FFFFFF",
    width: 24,
    height: 24
  },

  "@supports (height: 100dvh)": {
    height: "calc(100dvh)"
  }
}))

const EnlargedImageWrapper = styled("div")(() => ({
  width: "100vw",
  maxWidth: "var(--max-container-width)",
  height: "100vh",
  aspectRatio: 1,
  overflow: "hidden",
  position: "relative"
}))
